import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, Save, Send, Calendar, Users, MessageSquare, CheckCircle, FileText, Zap } from 'lucide-react';
import Layout from '../components/Layout';
import { campaignsAPI, groupsAPI, contactsAPI, templatesMetaAPI, instancesAPI, fluxosAPI, personasAPI } from '../services/api';
import api from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

const CampaignForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [instances, setInstances] = useState([]);
  // Personas: uma identidade que fala por vários números. Quando escolhida, o
  // alocador decide de qual número sai cada mensagem.
  const [personas, setPersonas] = useState([]);
  const [numerosDaPersona, setNumerosDaPersona] = useState([]);
  const [fluxos, setFluxos] = useState([]);
  const [gruposWhatsapp, setGruposWhatsapp] = useState([]);
  const [selectedGrupos, setSelectedGrupos] = useState([]);
  const [grupoSearch, setGrupoSearch] = useState('');
  const [grupoFilterTag, setGrupoFilterTag] = useState('all');
  const [grupoFilterCidade, setGrupoFilterCidade] = useState('all');
  const [grupoFilterRole, setGrupoFilterRole] = useState('all');
  const [grupoCategorias, setGrupoCategorias] = useState([]);
  const [grupoFilterCategorias, setGrupoFilterCategorias] = useState({});
  const [searchContact, setSearchContact] = useState('');
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [instanceUsage, setInstanceUsage] = useState(null);
  const [clienteConfig, setClienteConfig] = useState(null);

  // Pré-seleção vinda de outra tela (ex.: segmentação criada em Cobertura).
  // Sem isto, o botão "Criar e ir para campanha" abriria o formulário vazio e
  // o usuário teria que localizar a segmentação na lista manualmente.
  const grupoPreSelecionado = searchParams.get('grupo_id') || '';
  const origemPreSelecionada = searchParams.get('origem') || '';

  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    tipo: 'manual',
    tipo_origem: 'grupo',
    grupo_id: grupoPreSelecionado,
    contato_id: '',
    scheduled_date: '',
    instancia_id: '',
    persona_id: '',
    template_id: '',
    fluxo_id: '',
    tempo_expiracao_fluxo: 24,
  });

  /**
   * Como a campanha vai sair: '' (ainda não escolhido), 'unico' ou 'persona'.
   *
   * Estado próprio, e não derivado de persona_id, porque derivar fazia
   * "número único" parecer já escolhido e o select correspondente aparecia de
   * saída. Sem escolha explícita não se mostra select nenhum.
   */
  // 'unico' por padrão: sem persona, o toggle "Como enviar" nem aparece e o
  // modoEnvio nunca sairia de '', deixando o select de instância como 'hidden'
  // — cliente sem persona ficava sem canal de envio ao criar campanha.
  const [modoEnvio, setModoEnvio] = useState('unico');

  const instanciaSelecionada = instances.find(i => i.id === formData.instancia_id);
  const isZApi = instanciaSelecionada?.provider === 'z-api';
  /**
   * O bloco de template só aparece para Meta. No modo persona NÃO existe
   * instância selecionada, então derivar só de `instanciaSelecionada` escondia os
   * templates justamente na campanha de persona — que é onde o template é
   * obrigatório. Aqui a origem depende do modo.
   */
  const isMetaApi = formData.persona_id
    // Enquanto os números da persona carregam a lista está vazia; assumir Meta
    // evita o bloco de template aparecer piscando depois da requisição. Persona
    // só com número z-api (some() falso com lista já carregada) segue oculta.
    ? numerosDaPersona.length === 0 || numerosDaPersona.some(n => n.provider === 'meta-api')
    : instanciaSelecionada?.provider === 'meta-api';

  useEffect(() => {
    if (!user?.cliente_id) return;
    loadData();
    if (isEditing) {
      loadCampaign();
    }

    // Fechar dropdown ao clicar fora
    const handleClickOutside = (e) => {
      if (!e.target.closest('.contact-autocomplete')) {
        setShowContactDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [id, user?.cliente_id]);

  useEffect(() => {
    if (formData.instancia_id && instanciaSelecionada?.provider !== 'z-api') {
      instancesAPI.getUsage(formData.instancia_id)
        .then(r => setInstanceUsage(r.data))
        .catch(() => setInstanceUsage(null));
    } else {
      setInstanceUsage(null);
    }
  }, [formData.instancia_id]);

  // Números da persona escolhida: usados para filtrar template por aprovação e
  // para avisar quando o template não está aprovado em todos os números.
  useEffect(() => {
    if (!formData.persona_id) {
      setNumerosDaPersona([]);
      return;
    }
    personasAPI.get(formData.persona_id)
      .then(({ data }) => setNumerosDaPersona(data?.numeros || []))
      .catch(() => setNumerosDaPersona([]));
  }, [formData.persona_id]);

  const loadData = async () => {
    try {
      const [groupsRes, templatesRes, instancesRes, fluxosRes, personasRes] = await Promise.all([
        groupsAPI.list(user.cliente_id),
        templatesMetaAPI.list(),
        instancesAPI.list(user.cliente_id),
        fluxosAPI.list(user.cliente_id),
        personasAPI.list().catch(() => ({ data: [] })),
      ]);

      setGroups(Array.isArray(groupsRes.data) ? groupsRes.data : Array.isArray(groupsRes.data?.grupos) ? groupsRes.data.grupos : []);
      setTemplates(Array.isArray(templatesRes.data) ? templatesRes.data : []);
      setInstances(Array.isArray(instancesRes.data) ? instancesRes.data.filter(i => i.status === 'conectada' && i.platform === 'whatsapp') : []);
      // Só oferece persona ativa e que tenha número vinculado — persona vazia
      // não tem por onde enviar.
      setPersonas(Array.isArray(personasRes?.data) ? personasRes.data.filter(p => p.ativo && p.total_numeros > 0) : []);
      
      // Carregar grupos WhatsApp
      try {
        const gwRes = await api.get('/grupos-whatsapp', { params: { cliente_id: user.cliente_id } });
        const gwData = gwRes.data?.groups || (Array.isArray(gwRes.data) ? gwRes.data : []);
        setGruposWhatsapp(gwData.filter(g => g.status));
      } catch (e) {}

      // Carregar categorias de grupo
      try {
        const catRes = await api.get('/grupo-categorias');
        const catData = catRes.data?.data || catRes.data || [];
        setGrupoCategorias(Array.isArray(catData) ? catData : []);
      } catch (e) {}

      const fluxosData = Array.isArray(fluxosRes.data?.data) ? fluxosRes.data.data : Array.isArray(fluxosRes.data) ? fluxosRes.data : [];
      console.log('📊 Fluxos recebidos:', fluxosData);
      console.log('📊 Total de fluxos:', fluxosData.length);
      if (fluxosData.length > 0) {
        console.log('📊 Primeiro fluxo:', fluxosData[0]);
        console.log('📊 Status do primeiro:', fluxosData[0]?.status);
      }
      setFluxos(fluxosData);

      // Carregar config avançada do cliente
      try {
        const cRes = await api.get(`/clientes/${user.cliente_id}`);
        const c = cRes.data;
        if (c?.campanha_avancada_habilitada) {
          setClienteConfig(c);
        }
      } catch (e) {}
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setGroups([]);
      setContacts([]);
      setTemplates([]);
      setInstances([]);
      setFluxos([]);
    }
  };

  const loadCampaign = async () => {
    try {
      const response = await campaignsAPI.get(id);
      const campaign = response.data;
      // Campanha já existente tem a decisão tomada: reabre no modo correspondente,
      // senão os selects ficariam escondidos com os dados preenchidos.
      setModoEnvio(campaign.persona_id ? 'persona' : 'unico');
      setFormData({
        nome: campaign.nome || '',
        descricao: campaign.descricao || '',
        tipo: campaign.tipo || 'manual',
        tipo_origem: campaign.tipo_origem || (campaign.contato_id ? 'contato' : 'grupo'),
        grupo_id: campaign.grupo_id || '',
        contato_id: campaign.contato_id || '',
        scheduled_date: campaign.scheduled_date ? new Date(campaign.scheduled_date).toISOString().slice(0, 16) : '',
        instancia_id: campaign.instancia_id || '',
        persona_id: campaign.persona_id || '',
        template_id: campaign.template_id || '',
        fluxo_id: campaign.fluxo_id || '',
        tempo_expiracao_fluxo: campaign.tempo_expiracao_fluxo || 24,
      });
      
      // Preencher grupos WhatsApp selecionados ao editar campanha de grupos
      if (campaign.tipo_campanha === 'grupo_whatsapp' && campaign.grupos_destino?.length) {
        setSelectedGrupos(campaign.grupos_destino);
      }
      
      // Preencher campo de busca se tiver contato selecionado
      if (campaign.contato_id && campaign.contato) {
        setSearchContact(`${campaign.contato.nome} • ${campaign.contato.telefone}`);
      }
    } catch (error) {
      toast.error('Erro ao carregar campanha');
      navigate('/marketing/campanhas');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nome.trim()) {
      toast.error('Nome da campanha é obrigatório');
      return;
    }

    if (!modoEnvio) {
      toast.error('Escolha como a campanha vai ser enviada: número único ou persona');
      return;
    }
    if (!formData.persona_id && !formData.instancia_id) {
      toast.error('Selecione uma persona ou uma instância');
      return;
    }

    if (!formData.grupo_id && !formData.contato_id && selectedGrupos.length === 0) {
      toast.error('Selecione um grupo ou contato');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        nome: formData.nome,
        descricao: formData.descricao,
        tipo: formData.tipo,
        tipo_origem: isZApi ? 'grupo_whatsapp' : formData.tipo_origem,
        tipo_campanha: isZApi ? 'grupo_whatsapp' : 'individual',
        instancia_id: formData.instancia_id || null,
        persona_id: formData.persona_id || null,
        cliente_id: user.cliente_id,
        usuario_id: user.id,
        status: true,
      };

      if (isZApi) {
        payload.grupos_destino = selectedGrupos;
      } else {
        if (formData.grupo_id) payload.grupo_id = formData.grupo_id;
        if (formData.contato_id) payload.contato_id = formData.contato_id;
      }
      if (formData.scheduled_date) payload.scheduled_date = new Date(formData.scheduled_date).toISOString();
      if (formData.template_id) payload.template_id = formData.template_id;
      if (formData.fluxo_id) payload.fluxo_id = formData.fluxo_id;
      if (formData.tempo_expiracao_fluxo) payload.tempo_expiracao_fluxo = formData.tempo_expiracao_fluxo;

      // Configurações avançadas de disparo
      if (clienteConfig) {
        payload.perfil_disparo = formData.perfil_disparo || clienteConfig.perfil_disparo_padrao;
        payload.campanha_horario_inicio = formData.campanha_horario_inicio || clienteConfig.campanha_horario_inicio;
        payload.campanha_horario_fim = formData.campanha_horario_fim || clienteConfig.campanha_horario_fim;
        payload.campanha_delay_min = formData.campanha_delay_min ?? clienteConfig.campanha_delay_min;
        payload.campanha_delay_max = formData.campanha_delay_max ?? clienteConfig.campanha_delay_max;
        payload.campanha_lote_max_hora = formData.campanha_lote_max_hora ?? clienteConfig.campanha_lote_max_hora;
        payload.campanha_dias_semana = formData.campanha_dias_semana || clienteConfig.campanha_dias_semana;
      }

      if (isEditing) {
        await campaignsAPI.update(id, payload);
        toast.success('Campanha atualizada com sucesso!');
        navigate(`/marketing/campanhas/${id}`);
      } else {
        const response = await campaignsAPI.create(payload);
        toast.success('Campanha criada com sucesso!');
        const novaId = response.data?.id || response.data?.campanha?.id;
        navigate(novaId ? `/marketing/campanhas/${novaId}` : '/campaigns');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao salvar campanha');
    } finally {
      setLoading(false);
    }
  };

  // Com persona, o template precisa estar aprovado em ALGUM número dela — o
  // alocador exclui em tempo de envio os números sem aprovação.
  const idsDosNumeros = formData.persona_id
    ? numerosDaPersona.map(n => n.id)
    : [formData.instancia_id];

  const aprovadoEm = (t, id) =>
    (t.status === 'aprovado' && t.instancia_id === id)
    || t.instancias?.some(ti => (ti.instancia_id || ti.instancia?.id) === id && ti.status === 'aprovado');

  const templatesDisponiveis = templates.filter(t => idsDosNumeros.some(id => id && aprovadoEm(t, id)));

  // Aviso honesto: número sem o template aprovado NÃO envia, então a capacidade
  // real da persona é menor do que o número de números vinculados.
  const numerosSemTemplate = formData.persona_id && formData.template_id
    ? numerosDaPersona.filter(n => !aprovadoEm(templates.find(t => t.id === formData.template_id) || {}, n.id))
    : [];

  const grupoSelecionado = groups.find(g => g.id === formData.grupo_id);
  const contatoSelecionado = contacts.find(c => c.id === formData.contato_id);
  const totalDestinatarios = grupoSelecionado ? (grupoSelecionado.totalContatos || grupoSelecionado._count?.contatos || 0) : formData.contato_id ? 1 : 0;
  const excedeLimiteNumero = instanceUsage?.messaging_limit && totalDestinatarios > instanceUsage.messaging_limit.remaining;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center space-x-4 mb-8">
          <button
            onClick={() => navigate('/marketing/campanhas')}
            className={`p-3 rounded-xl transition-colors ${
              darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-100'
            }`}
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {isEditing ? 'Editar Campanha' : 'Nova Campanha'}
            </h1>
            <p className={`text-base mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Configure sua campanha de marketing
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Coluna Principal - 2/3 */}
            <div className="lg:col-span-2 space-y-8">
              {/* Informações Básicas */}
              <div className={`p-8 rounded-xl border shadow-sm ${
                darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'
              }`}>
                <h3 className={`text-xl font-bold mb-6 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <MessageSquare className="w-6 h-6 mr-3 text-blue-500" />
                  Informações Básicas
                </h3>
                <div className="space-y-6">
                  <div>
                    <label className={`block text-base font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Nome da Campanha *
                    </label>
                    <input
                      type="text"
                      value={formData.nome}
                      onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                      className={`w-full px-4 py-3 rounded-xl border text-base ${
                        darkMode
                          ? 'bg-zinc-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      placeholder="Ex: Promoção Black Friday"
                      required
                    />
                  </div>

                  <div>
                    <label className={`block text-base font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Descrição
                    </label>
                    <textarea
                      value={formData.descricao}
                      onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                      rows={4}
                      className={`w-full px-4 py-3 rounded-xl border text-base ${
                        darkMode
                          ? 'bg-zinc-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      placeholder="Descreva o objetivo desta campanha..."
                    />
                  </div>
                </div>
              </div>

              {/* Canal de Envio */}
              <div className={`p-8 rounded-xl border shadow-sm ${
                darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'
              }`}>
                <h3 className={`text-xl font-bold mb-6 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <Send className="w-6 h-6 mr-3 text-green-500" />
                  Canal de Envio
                </h3>
                {personas.length > 0 && (
                  <div className="mb-5">
                    <label className={`block text-base font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Como enviar
                    </label>
                    <div className="flex flex-col sm:flex-row gap-3 mb-3">
                      <button
                        type="button"
                        onClick={() => {
                          setModoEnvio('unico');
                          setFormData({ ...formData, persona_id: '', template_id: '' });
                        }}
                        className={`flex-1 px-4 py-3 rounded-xl border text-left text-sm ${
                          modoEnvio === 'unico'
                            ? 'border-nomoz-orange bg-nomoz-orange/10'
                            : darkMode ? 'border-gray-600' : 'border-gray-300'
                        }`}
                      >
                        <span className={`block font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Número único</span>
                        <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Toda a campanha sai de um só número</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setModoEnvio('persona');
                          setFormData({ ...formData, instancia_id: '', template_id: '' });
                        }}
                        className={`flex-1 px-4 py-3 rounded-xl border text-left text-sm ${
                          modoEnvio === 'persona'
                            ? 'border-nomoz-orange bg-nomoz-orange/10'
                            : darkMode ? 'border-gray-600' : 'border-gray-300'
                        }`}
                      >
                        <span className={`block font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Persona (vários números)</span>
                        <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Distribui entre os números da persona</span>
                      </button>
                    </div>

                    {modoEnvio === 'persona' && (
                    <select
                      value={formData.persona_id}
                      onChange={(e) => setFormData({ ...formData, persona_id: e.target.value, instancia_id: '', template_id: '' })}
                      className={`w-full px-4 py-3 rounded-xl border text-base ${
                        darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    >
                      <option value="">— escolher persona —</option>
                      {personas.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.nome} • {p.total_numeros} número(s)
                        </option>
                      ))}
                    </select>
                    )}

                    {formData.persona_id && numerosDaPersona.length > 0 && (
                      <p className={`mt-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        A mesma pessoa sempre recebe do mesmo número. Números em qualidade
                        ruim, pausados ou sem o template aprovado ficam fora automaticamente.
                      </p>
                    )}

                    {numerosSemTemplate.length > 0 && (
                      <div className="mt-3 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-600 dark:text-amber-400 text-sm">
                        ⚠️ O template não está aprovado em <strong>{numerosSemTemplate.length}</strong> de{' '}
                        <strong>{numerosDaPersona.length}</strong> número(s) desta persona
                        ({numerosSemTemplate.map(n => n.rotulo_interno || n.nome_instancia).join(', ')}).
                        Esses números não vão enviar, então a capacidade real é menor. Use
                        "Sincronizar templates" na tela de Personas.
                      </div>
                    )}
                  </div>
                )}

                <div className={modoEnvio === 'unico' ? '' : 'hidden'}>
                  <label className={`block text-base font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Selecione a Instância *
                  </label>
                  <select
                    value={formData.instancia_id}
                    onChange={(e) => setFormData({ ...formData, instancia_id: e.target.value, template_id: '' })}
                    className={`w-full px-4 py-3 rounded-xl border text-base ${
                      darkMode
                        ? 'bg-zinc-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    required={modoEnvio === 'unico'}
                  >
                    <option value="">Escolha uma instância</option>
                    {instances.filter(i => i.platform !== 'instagram').map((instance) => (
                      <option key={instance.id} value={instance.id}>
                        {instance.nome_instancia} • {instance.provider === 'z-api' ? 'Z-API (Grupos)' : 'Meta API (Individual)'}
                      </option>
                    ))}
                  </select>
                </div>
                {excedeLimiteNumero && (
                  <div className="mt-4 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-600 dark:text-amber-400 text-sm">
                    ⚠️ O segmento tem <strong>{totalDestinatarios}</strong> contatos, mas este número só pode enviar para mais <strong>{instanceUsage.messaging_limit.remaining}</strong> nas próximas 24h (tier {instanceUsage.messaging_limit.tier}, limite {instanceUsage.messaging_limit.limit.toLocaleString('pt-BR')}). A campanha será bloqueada na execução.
                  </div>
                )}
              </div>
              <div className={`p-8 rounded-xl border shadow-sm ${
                darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'
              }`}>
                <h3 className={`text-xl font-bold mb-6 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <Users className="w-6 h-6 mr-3 text-purple-500" />
                  {isZApi ? 'Grupos WhatsApp Destino' : 'Destinatários'}
                </h3>

                {isZApi ? (
                  /* === SELETOR DE GRUPOS WHATSAPP === */
                  <div>
                    <label className={`block text-base font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Grupos destino
                    </label>
                    {/* Busca e filtros */}
                    <div className="flex gap-2 mb-2 flex-wrap">
                      <input type="text" placeholder="Buscar por nome, tag, cidade..."
                        value={grupoSearch} onChange={(e) => setGrupoSearch(e.target.value)}
                        className={`flex-1 min-w-[180px] px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`} />
                      <select value={grupoFilterTag} onChange={(e) => setGrupoFilterTag(e.target.value)}
                        className={`px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>
                        <option value="all">🏷️ Todas tags</option>
                        {[...new Set(gruposWhatsapp.flatMap(g => g.tags || []))].sort().map(tag => (
                          <option key={tag} value={tag}>#{tag}</option>
                        ))}
                      </select>
                      <select value={grupoFilterCidade} onChange={(e) => setGrupoFilterCidade(e.target.value)}
                        className={`px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>
                        <option value="all">📍 Todas cidades</option>
                        {[...new Set(gruposWhatsapp.map(g => g.cidade).filter(Boolean))].sort().map(cidade => (
                          <option key={cidade} value={cidade}>{cidade}</option>
                        ))}
                      </select>
                      <select value={grupoFilterRole} onChange={(e) => setGrupoFilterRole(e.target.value)}
                        className={`px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>
                        <option value="all">👥 Todos</option>
                        <option value="admin">👑 Admin</option>
                        <option value="member">👤 Membro</option>
                      </select>
                      {grupoCategorias.map(cat => (
                        <select key={cat.id} value={grupoFilterCategorias[cat.id] || 'all'}
                          onChange={(e) => setGrupoFilterCategorias({ ...grupoFilterCategorias, [cat.id]: e.target.value === 'all' ? undefined : e.target.value })}
                          className={`px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>
                          <option value="all">{cat.label}</option>
                          <option value="__none__">Sem {cat.label.toLowerCase()}</option>
                          {(cat.opcoes || []).map(opc => (
                            <option key={opc.id} value={opc.id}>{opc.nome}</option>
                          ))}
                        </select>
                      ))}
                    </div>
                    {/* Barra de ações */}
                    <div className={`flex items-center justify-between py-2 px-1 mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <span className="text-xs font-medium">
                        {selectedGrupos.length > 0 
                          ? `✅ ${selectedGrupos.length} selecionado${selectedGrupos.length > 1 ? 's' : ''}`
                          : 'Nenhum selecionado'}
                      </span>
                      <div className="flex gap-3">
                        <button type="button" onClick={() => {
                          const filtered = gruposWhatsapp.filter(g => {
                            const search = grupoSearch.toLowerCase();
                            const matchSearch = !search || g.nome?.toLowerCase().includes(search) || (g.tags || []).some(t => t.includes(search)) || g.cidade?.toLowerCase().includes(search) || g.descricao?.toLowerCase().includes(search);
                            const matchTag = grupoFilterTag === 'all' || (g.tags || []).includes(grupoFilterTag);
                            const matchCidade = grupoFilterCidade === 'all' || g.cidade === grupoFilterCidade;
                            const matchRole = grupoFilterRole === 'all' || (grupoFilterRole === 'admin' ? g.is_admin : !g.is_admin);
                            const matchCat = Object.entries(grupoFilterCategorias).every(([catId, opcId]) => {
                              if (!opcId) return true;
                              if (opcId === '__none__') return !(g.categorias_valores || []).some(v => v.categoria_id === catId);
                              return (g.categorias_valores || []).some(v => v.opcao_id === opcId);
                            });
                            return matchSearch && matchTag && matchCidade && matchRole && matchCat;
                          });
                          setSelectedGrupos(filtered.map(g => g.id));
                        }} className="text-xs font-medium text-nomoz-orange hover:underline">
                          Selecionar visíveis
                        </button>
                        {selectedGrupos.length > 0 && (
                          <button type="button" onClick={() => setSelectedGrupos([])}
                            className="text-xs font-medium text-red-500 hover:underline">
                            Limpar seleção
                          </button>
                        )}
                      </div>
                    </div>
                    <div className={`max-h-64 overflow-y-auto rounded-xl border ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                      {gruposWhatsapp.length === 0 ? (
                        <div className={`p-4 text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Nenhum grupo encontrado. Sincronize os grupos primeiro.
                        </div>
                      ) : gruposWhatsapp.filter(g => {
                        const search = grupoSearch.toLowerCase();
                        const matchSearch = !search || g.nome?.toLowerCase().includes(search) || (g.tags || []).some(t => t.includes(search)) || g.cidade?.toLowerCase().includes(search) || g.descricao?.toLowerCase().includes(search);
                        const matchTag = grupoFilterTag === 'all' || (g.tags || []).includes(grupoFilterTag);
                        const matchCidade = grupoFilterCidade === 'all' || g.cidade === grupoFilterCidade;
                        const matchRole = grupoFilterRole === 'all' || (grupoFilterRole === 'admin' ? g.is_admin : !g.is_admin);
                        const matchCat = Object.entries(grupoFilterCategorias).every(([catId, opcId]) => {
                          if (!opcId) return true;
                          if (opcId === '__none__') return !(g.categorias_valores || []).some(v => v.categoria_id === catId);
                          return (g.categorias_valores || []).some(v => v.opcao_id === opcId);
                        });
                        return matchSearch && matchTag && matchCidade && matchRole && matchCat;
                      }).map(g => (
                        <label key={g.id} className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                          selectedGrupos.includes(g.id)
                            ? darkMode ? 'bg-orange-900/20' : 'bg-orange-50'
                            : darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-50'
                        } ${darkMode ? 'border-b border-zinc-700' : 'border-b border-gray-100'}`}>
                          <input type="checkbox" checked={selectedGrupos.includes(g.id)}
                            onChange={(e) => {
                              if (e.target.checked) setSelectedGrupos([...selectedGrupos, g.id]);
                              else setSelectedGrupos(selectedGrupos.filter(id => id !== g.id));
                            }}
                            className="w-4 h-4 rounded border-gray-300 text-nomoz-orange focus:ring-nomoz-orange" />
                          <div className="flex-1 min-w-0">
                            <div className={`text-sm font-medium truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>{g.nome}</div>
                            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {g.total_participantes || 0} participantes
                              {g.cidade && ` · 📍${g.cidade}`}
                              {(g.tags || []).length > 0 && ` · ${g.tags.map(t => '#'+t).join(' ')}`}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ) : (
                  /* === DESTINATÁRIOS META API (existente) === */
                  <>
                {/* Seleção de Tipo */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, tipo_origem: 'grupo', contato_id: '' })}
                    className={`relative p-6 rounded-xl border-2 transition-all text-left ${
                      formData.tipo_origem === 'grupo'
                        ? darkMode
                          ? 'border-purple-500 bg-purple-900/20 shadow-lg'
                          : 'border-purple-500 bg-purple-50 shadow-lg'
                        : darkMode
                        ? 'border-gray-700 hover:border-gray-600 hover:bg-zinc-700/50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <Users className={`w-8 h-8 mb-3 ${
                      formData.tipo_origem === 'grupo' ? 'text-purple-500' : darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`} />
                    <div className={`font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Grupo
                    </div>
                    <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Enviar para múltiplos contatos
                    </div>
                    {formData.tipo_origem === 'grupo' && (
                      <CheckCircle className="absolute top-4 right-4 w-6 h-6 text-purple-500" />
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, tipo_origem: 'contato', grupo_id: '' })}
                    className={`relative p-6 rounded-xl border-2 transition-all text-left ${
                      formData.tipo_origem === 'contato'
                        ? darkMode
                          ? 'border-purple-500 bg-purple-900/20 shadow-lg'
                          : 'border-purple-500 bg-purple-50 shadow-lg'
                        : darkMode
                        ? 'border-gray-700 hover:border-gray-600 hover:bg-zinc-700/50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <MessageSquare className={`w-8 h-8 mb-3 ${
                      formData.tipo_origem === 'contato' ? 'text-purple-500' : darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`} />
                    <div className={`font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Contato Individual
                    </div>
                    <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Enviar para uma pessoa
                    </div>
                    {formData.tipo_origem === 'contato' && (
                      <CheckCircle className="absolute top-4 right-4 w-6 h-6 text-purple-500" />
                    )}
                  </button>
                </div>

                {/* Select baseado na escolha */}
                <div>
                  {formData.tipo_origem === 'grupo' ? (
                    <div>
                      <label className={`block text-base font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Selecione o Grupo
                      </label>

                      {/* Contexto de origem: o público veio pronto de Cobertura */}
                      {origemPreSelecionada === 'cobertura' && grupoPreSelecionado && (
                        <div className={`mb-3 rounded-lg p-3 flex items-start gap-2 ${
                          darkMode ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'
                        }`}>
                          <Users size={15} className="text-blue-500 mt-0.5 shrink-0" />
                          <div className={`text-xs ${darkMode ? 'text-blue-200' : 'text-blue-800'}`}>
                            <p>
                              Segmentação criada em <strong>Cobertura</strong> e já selecionada abaixo
                              {grupoSelecionado?.totalContatos ? ` com ${grupoSelecionado.totalContatos} contatos` : ''}.
                              Confira o público antes de enviar.
                            </p>
                            {grupoSelecionado?.semOptIn > 0 && (
                              <p className="mt-1 font-medium">
                                ⚠️ {grupoSelecionado.semOptIn} destes contatos não têm opt-in.
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      <select
                        value={formData.grupo_id}
                        onChange={(e) => setFormData({ ...formData, grupo_id: e.target.value })}
                        className={`w-full px-4 py-3 rounded-xl border text-base ${
                          darkMode
                            ? 'bg-zinc-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        } focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                      >
                        <option value="">Escolha um grupo</option>
                        {groups.map((group) => (
                          <option key={group.id} value={group.id}>
                            {group.nome} • {group.totalContatos || group._count?.contatos || 0} contatos
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="relative contact-autocomplete">
                      <label className={`block text-base font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Buscar Contato
                      </label>
                      <input
                        type="text"
                        value={searchContact}
                        onChange={(e) => {
                          const val = e.target.value;
                          setSearchContact(val);
                          setShowContactDropdown(true);
                          if (val.length >= 2) {
                            clearTimeout(window._contactSearchTimer);
                            window._contactSearchTimer = setTimeout(() => {
                              contactsAPI.autocomplete(user.cliente_id, val)
                                .then(r => setContacts(Array.isArray(r.data) ? r.data : []))
                                .catch(() => {});
                            }, 300);
                          } else {
                            setContacts([]);
                          }
                        }}
                        onFocus={() => setShowContactDropdown(true)}
                        placeholder="Digite nome ou telefone..."
                        className={`w-full px-4 py-3 rounded-xl border text-base ${
                          darkMode
                            ? 'bg-zinc-700 border-gray-600 text-white placeholder-gray-400'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                        } focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                      />
                      
                      {/* Dropdown de resultados */}
                      {showContactDropdown && (() => {
                        const filtered = contacts;
                        
                        return filtered.length > 0 ? (
                          <div className={`absolute z-10 w-full mt-2 rounded-xl border shadow-lg max-h-60 overflow-y-auto ${
                            darkMode ? 'bg-zinc-700 border-gray-600' : 'bg-white border-gray-200'
                          }`}>
                            {filtered.slice(0, 50).map((contact) => (
                              <button
                                key={contact.id}
                                type="button"
                                onClick={() => {
                                  setFormData({ ...formData, contato_id: contact.id });
                                  setSearchContact(`${contact.nome} • ${contact.telefone}`);
                                  setShowContactDropdown(false);
                                }}
                                className={`w-full text-left px-4 py-3 hover:bg-purple-500/10 transition-colors border-b last:border-b-0 ${
                                  darkMode ? 'border-gray-600' : 'border-gray-100'
                                }`}
                              >
                                <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {contact.nome}
                                </div>
                                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                  {contact.telefone}
                                </div>
                              </button>
                            ))}
                          </div>
                        ) : searchContact ? (
                          <div className={`absolute z-10 w-full mt-2 rounded-xl border shadow-lg p-4 text-center ${
                            darkMode ? 'bg-zinc-700 border-gray-600 text-gray-400' : 'bg-white border-gray-200 text-gray-600'
                          }`}>
                            Nenhum contato encontrado
                          </div>
                        ) : null;
                      })()}
                      
                      {formData.contato_id && (
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, contato_id: '' });
                            setSearchContact('');
                          }}
                          className={`mt-2 text-sm ${darkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'}`}
                        >
                          Limpar seleção
                        </button>
                      )}
                    </div>
                  )}
                </div>
                  </>
                )}
              </div>

              {/* Tipo de Execução */}
              <div className={`p-8 rounded-xl border shadow-sm ${
                darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'
              }`}>
                <h3 className={`text-xl font-bold mb-6 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <Calendar className="w-6 h-6 mr-3 text-orange-500" />
                  Quando Executar
                </h3>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {[
                      { value: 'manual', label: 'Manual', icon: Send, desc: 'Executar quando você quiser' },
                      { value: 'agendada', label: 'Agendada', icon: Calendar, desc: 'Executar em data específica' },
                    ].map((option) => {
                      const Icon = option.icon;
                      return (
                        <label
                          key={option.value}
                          className={`relative flex flex-col p-6 rounded-xl border-2 cursor-pointer transition-all ${
                            formData.tipo === option.value
                              ? darkMode
                                ? 'border-blue-500 bg-blue-900/20 shadow-lg'
                                : 'border-blue-500 bg-blue-50 shadow-lg'
                              : darkMode
                              ? 'border-gray-700 hover:border-gray-600 hover:bg-zinc-700/50'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="radio"
                            name="tipo"
                            value={option.value}
                            checked={formData.tipo === option.value}
                            onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                            className="sr-only"
                          />
                          <Icon className={`w-10 h-10 mb-4 ${
                            formData.tipo === option.value ? 'text-blue-500' : darkMode ? 'text-gray-400' : 'text-gray-600'
                          }`} />
                          <span className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {option.label}
                          </span>
                          <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {option.desc}
                          </span>
                          {formData.tipo === option.value && (
                            <CheckCircle className="absolute top-4 right-4 w-6 h-6 text-blue-500" />
                          )}
                        </label>
                      );
                    })}
                  </div>

                  {formData.tipo === 'agendada' && (
                    <div className="pt-4">
                      <label className={`block text-base font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Data e Hora do Agendamento
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.scheduled_date}
                        onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                        className={`w-full px-4 py-3 rounded-xl border text-base ${
                          darkMode
                            ? 'bg-zinc-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      />
                    </div>
                  )}

                  {/* Configurações Avançadas de Disparo */}
                  {clienteConfig && (
                    <div className={`mt-6 pt-6 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <h4 className={`font-semibold mb-4 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        🚀 Velocidade de Disparo
                      </h4>
                      
                      {/* Perfil */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                        {[
                          { id: 'gradual', label: '🐢 Gradual', desc: '60-120s, 20/h', delay_min: 60, delay_max: 120, lote: 20 },
                          { id: 'moderado', label: '⚡ Moderado', desc: '15-30s, 60/h', delay_min: 15, delay_max: 30, lote: 60 },
                          { id: 'agressivo', label: '🔥 Agressivo', desc: '5-10s, 150/h', delay_min: 5, delay_max: 10, lote: 150 },
                          { id: 'personalizado', label: '🎯 Custom', desc: 'Valores custom' },
                        ].map(perfil => {
                          const liberados = clienteConfig.perfis_disparo_liberados || ['gradual'];
                          const travado = !liberados.includes(perfil.id);
                          return (
                          <button
                            key={perfil.id}
                            type="button"
                            disabled={travado}
                            onClick={() => {
                              if (travado) return;
                              const update = { perfil_disparo: perfil.id };
                              if (perfil.delay_min) {
                                update.campanha_delay_min = perfil.delay_min;
                                update.campanha_delay_max = perfil.delay_max;
                                update.campanha_lote_max_hora = perfil.lote;
                              }
                              setFormData({ ...formData, ...update });
                            }}
                            className={`p-3 rounded-xl border-2 text-center transition ${
                              travado
                                ? 'opacity-40 cursor-not-allowed border-gray-300 dark:border-gray-700'
                                : (formData.perfil_disparo || clienteConfig.perfil_disparo_padrao) === perfil.id
                                  ? 'border-nomoz-orange bg-nomoz-orange/10'
                                  : darkMode ? 'border-gray-700 hover:border-gray-600' : 'border-gray-200 hover:border-gray-300'
                            }`}
                            title={travado ? 'Este perfil não está liberado para sua conta. Entre em contato com o suporte.' : ''}
                          >
                            <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {travado ? '🔒 ' : ''}{perfil.label}
                            </p>
                            <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {(formData.perfil_disparo || clienteConfig.perfil_disparo_padrao) === perfil.id && perfil.id !== 'personalizado'
                                ? `${formData.campanha_delay_min ?? clienteConfig.campanha_delay_min ?? perfil.delay_min}-${formData.campanha_delay_max ?? clienteConfig.campanha_delay_max ?? perfil.delay_max}s, ${formData.campanha_lote_max_hora ?? clienteConfig.campanha_lote_max_hora ?? perfil.lote}/h`
                                : perfil.desc}
                            </p>
                          </button>
                          );
                        })}
                      </div>

                      {/* Janela de horário + delay */}
                      {/* Janela de horário + delay */}
                      {(() => {
                        const liberados = clienteConfig.perfis_disparo_liberados || ['gradual'];
                        const customLiberado = liberados.includes('personalizado');
                        const perfilAtual = formData.perfil_disparo || clienteConfig.perfil_disparo_padrao;
                        const editavel = customLiberado && perfilAtual === 'personalizado';
                        return (
                      <div className={`${!editavel ? 'opacity-50 pointer-events-none select-none' : ''}`}>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div title="Horário a partir do qual as mensagens começam a ser enviadas. Antes desse horário, a campanha fica pausada.">
                          <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Início ℹ️</label>
                          <input type="time" value={formData.campanha_horario_inicio || clienteConfig.campanha_horario_inicio || '08:00'}
                            onChange={(e) => setFormData({ ...formData, campanha_horario_inicio: e.target.value })}
                            className={`w-full rounded-xl px-3 py-2 border ${darkMode ? 'bg-zinc-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`} />
                        </div>
                        <div title="Horário limite para envio. Após esse horário, a campanha pausa e retoma no próximo dia permitido.">
                          <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Fim ℹ️</label>
                          <input type="time" value={formData.campanha_horario_fim || clienteConfig.campanha_horario_fim || '19:00'}
                            onChange={(e) => setFormData({ ...formData, campanha_horario_fim: e.target.value })}
                            className={`w-full rounded-xl px-3 py-2 border ${darkMode ? 'bg-zinc-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`} />
                        </div>
                        <div title="Tempo mínimo (em segundos) entre cada mensagem enviada. Quanto maior, mais lento e seguro o envio.">
                          <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Delay Min (s) ℹ️</label>
                          <input type="number" value={formData.campanha_delay_min ?? clienteConfig.campanha_delay_min ?? 15}
                            onChange={(e) => setFormData({ ...formData, campanha_delay_min: parseInt(e.target.value) })}
                            className={`w-full rounded-xl px-3 py-2 border ${darkMode ? 'bg-zinc-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`} />
                        </div>
                        <div title="Tempo máximo (em segundos) entre cada mensagem. O intervalo real será aleatório entre o mínimo e máximo, simulando comportamento humano.">
                          <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Delay Máx (s) ℹ️</label>
                          <input type="number" value={formData.campanha_delay_max ?? clienteConfig.campanha_delay_max ?? 30}
                            onChange={(e) => setFormData({ ...formData, campanha_delay_max: parseInt(e.target.value) })}
                            className={`w-full rounded-xl px-3 py-2 border ${darkMode ? 'bg-zinc-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`} />
                        </div>
                        <div title="Número máximo de mensagens que serão enviadas por hora. Ao atingir este limite, a campanha pausa até a próxima hora.">
                          <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Máx/Hora ℹ️</label>
                          <input type="number" value={formData.campanha_lote_max_hora ?? clienteConfig.campanha_lote_max_hora ?? 60}
                            onChange={(e) => setFormData({ ...formData, campanha_lote_max_hora: parseInt(e.target.value) })}
                            className={`w-full rounded-xl px-3 py-2 border ${darkMode ? 'bg-zinc-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`} />
                        </div>
                      </div>

                      {/* Dias da semana */}
                      <div className="mt-4 flex gap-2">
                        {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((dia, i) => {
                          const dias = formData.campanha_dias_semana || clienteConfig.campanha_dias_semana || [1,2,3,4,5];
                          const ativo = dias.includes(i);
                          return (
                            <button key={i} type="button"
                              onClick={() => {
                                const novosDias = ativo ? dias.filter(d => d !== i) : [...dias, i].sort();
                                setFormData({ ...formData, campanha_dias_semana: novosDias });
                              }}
                              className={`px-3 py-2 rounded-xl text-sm font-medium transition ${
                                ativo ? 'bg-nomoz-orange text-white' : darkMode ? 'bg-zinc-700 text-gray-400 hover:bg-zinc-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                              }`}
                            >
                              {dia}
                            </button>
                          );
                        })}
                      </div>

                      {!editavel && (
                        <p className={`text-xs mt-2 ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                          🔒 Selecione o perfil "Custom" para editar os valores manualmente.
                        </p>
                      )}
                      </div>
                        );
                      })()}

                      <p className={`text-sm mt-3 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        As mensagens serão distribuídas dentro da janela de horário com intervalo aleatório entre cada envio.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar - 1/3 */}
            <div className="lg:sticky lg:top-6 space-y-6" style={{ height: 'fit-content' }}>
              {/* Resumo */}
              <div className={`p-6 rounded-xl border shadow-sm ${
                darkMode 
                  ? 'bg-gradient-to-br from-blue-900/20 to-purple-900/20 border-gray-700' 
                  : 'bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200'
              }`}>
                <h3 className={`font-bold text-lg mb-4 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <CheckCircle className="w-5 h-5 mr-2 text-blue-500" />
                  Resumo
                </h3>
                <div className="space-y-4 text-sm">
                  <div>
                    <span className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Nome
                    </span>
                    <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {formData.nome || '-'}
                    </p>
                  </div>
                  <div>
                    <span className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Instância
                    </span>
                    <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {instanciaSelecionada?.nome_instancia || '-'}
                    </p>
                  </div>
                  <div>
                    <span className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Destinatários
                    </span>
                    <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {isZApi && selectedGrupos.length > 0 
                        ? `${selectedGrupos.length} grupo${selectedGrupos.length > 1 ? 's' : ''} WhatsApp`
                        : grupoSelecionado ? `Grupo: ${grupoSelecionado.nome}` : 
                       formData.contato_id ? `Contato: ${searchContact || contatoSelecionado?.nome || ''}` : '-'}
                    </p>
                  </div>
                  <div>
                    <span className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Tipo
                    </span>
                    <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {formData.tipo === 'manual' ? 'Manual' : 'Agendada'}
                    </p>
                  </div>
                  {formData.fluxo_id && (
                    <div>
                      <span className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Fluxo
                      </span>
                      <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {fluxos.find(f => f.id === formData.fluxo_id)?.nome || '-'}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Template (Meta API) */}
              {isMetaApi && (
                <div className={`p-6 rounded-xl border shadow-sm ${
                  darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                  <h3 className={`font-bold text-base mb-4 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <FileText className="w-5 h-5 mr-2 text-green-500" />
                    Template
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Usar Template Aprovado
                      </label>
                      <select
                        value={formData.template_id}
                        onChange={(e) => setFormData({ ...formData, template_id: e.target.value })}
                        className={`w-full px-3 py-2 rounded-lg border text-sm ${
                          darkMode
                            ? 'bg-zinc-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      >
                        <option value="">Criar nova mensagem</option>
                        {templatesDisponiveis.length === 0 ? (
                          <option value="" disabled>Nenhum template aprovado</option>
                        ) : (
                          templatesDisponiveis.map((template) => (
                            <option key={template.id} value={template.id}>
                              {template.nome}
                            </option>
                          ))
                        )}
                      </select>
                      <p className={`mt-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Opcional. Templates são específicos por instância.
                      </p>
                    </div>

                    {/* Preview do Template */}
                    {formData.template_id && (() => {
                      const templateSelecionado = templatesDisponiveis.find(t => t.id === formData.template_id);
                      return templateSelecionado ? (
                        <div className={`p-4 rounded-lg ${darkMode ? 'bg-zinc-900' : 'bg-[#e5ddd5]'}`}>
                          <div className={`text-xs font-medium mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>📱 Preview WhatsApp</div>
                          <div className="relative max-w-[85%]">
                            <div className="bg-[#DCF8C6] text-gray-900 rounded-lg shadow-sm relative overflow-hidden">
                              {templateSelecionado.media_url && templateSelecionado.media_type === 'IMAGE' && (
                                <img src={templateSelecionado.media_url} alt="Mídia" className="w-full max-h-48 object-cover" />
                              )}
                              {templateSelecionado.media_url && templateSelecionado.media_type === 'VIDEO' && (
                                <video src={templateSelecionado.media_url} controls className="w-full max-h-48" />
                              )}
                              <div className="p-2">
                                <div className="text-sm whitespace-pre-wrap break-words" style={{ wordBreak: 'break-word' }}>
                                  {templateSelecionado.mensagem}
                                </div>
                                <div className="text-[10px] text-gray-600 text-right mt-1">
                                  {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                </div>
                              </div>
                              <div className="absolute bottom-0 right-[-8px] w-0 h-0 border-l-[8px] border-l-[#DCF8C6] border-b-[8px] border-b-transparent"></div>
                            </div>
                          </div>
                        </div>
                      ) : null;
                    })()}
                  </div>
                </div>
              )}

              {/* Fluxo de Conversa — só para campanhas individuais */}
              {!isZApi && <div className={`p-6 rounded-xl border shadow-sm ${
                darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'
              }`}>
                <h3 className={`font-bold text-base mb-4 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <Zap className="w-5 h-5 mr-2 text-yellow-500" />
                  Fluxo de Conversa
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Fluxo Automático (Opcional)
                    </label>
                    <select
                      value={formData.fluxo_id}
                      onChange={(e) => setFormData({ ...formData, fluxo_id: e.target.value })}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${
                        darkMode
                          ? 'bg-zinc-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    >
                      <option value="">Sem fluxo automático</option>
                      {fluxos.filter(f => f.ativo).map((fluxo) => (
                        <option key={fluxo.id} value={fluxo.id}>
                          {fluxo.nome}
                        </option>
                      ))}
                    </select>
                    {fluxos.length === 0 && (
                      <p className={`mt-2 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        Nenhum fluxo disponível. Crie um fluxo em Configurações.
                      </p>
                    )}
                    {fluxos.length > 0 && (
                      <p className={`mt-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Fluxo será ativado após o envio da primeira mensagem
                      </p>
                    )}
                  </div>

                  {formData.fluxo_id && (
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Tempo de Expiração (horas)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="720"
                        value={formData.tempo_expiracao_fluxo}
                        onChange={(e) => setFormData({ ...formData, tempo_expiracao_fluxo: parseInt(e.target.value) })}
                        className={`w-full px-3 py-2 rounded-lg border text-sm ${
                          darkMode
                            ? 'bg-zinc-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      />
                      <p className={`mt-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Após esse período, o fluxo expira (padrão: 24h)
                      </p>
                    </div>
                  )}
                </div>
              </div>}

              {/* Ações */}
              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full gradient-button py-4 rounded-xl font-semibold text-white text-base disabled:opacity-50 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transition-all"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Salvando...</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5" />
                      <span>{isEditing ? 'Atualizar' : 'Criar'} Campanha</span>
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/marketing/campanhas')}
                  className={`w-full py-3 rounded-xl font-medium transition-colors ${
                    darkMode
                      ? 'bg-zinc-700 hover:bg-zinc-600 text-white'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                  }`}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default CampaignForm;
